import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import {
  collapseMobileHeaderShell,
  lockScroll,
  onAnimationEnd,
  removeWillChangeOnAnimationEnd,
  restoreMobileHeaderShell,
  unlockScroll,
} from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];
  #drawerAnimation = null;
  #drawerContentAnimations = [];
  #mobileOpenRevision = 0;
  #openSearchAfterClose = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    // If the user tapped before this module finished loading, native <details>
    // may already be open. Complete the same animated opening path instead of
    // leaving an unstyled or delayed drawer on screen.
    if (this.refs.details.open && !this.refs.details.classList.contains('menu-open')) {
      this.open();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#mobileOpenRevision += 1;
    this.#cancelMobileAnimations();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    const state = this.refs.details.dataset.stylistDrawerState;
    return (
      this.refs.details.hasAttribute('open') ||
      state === 'preparing' ||
      state === 'opening' ||
      state === 'open'
    );
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle(event) {
    event?.preventDefault();
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (this.#isMobileMainDrawer(details)) {
      this.#prepareMobileDrawerOpen(details, target, summary);
      return;
    }

    details.open = true;
    details.classList.remove('menu-open');
    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);

    // Commit the fully off-canvas start frame before applying the open state.
    void this.refs.menuDrawer.offsetWidth;
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Move from the mobile menu to the existing search dialog without briefly
   * restoring the header shell between the two overlays.
   * @param {Event} [event]
   */
  openSearch(event) {
    event?.preventDefault();
    const searchDialog = document.getElementById('search-modal');
    if (!searchDialog || typeof searchDialog.showDialog !== 'function') return;

    if (this.#isMobileMainDrawer(this.refs.details) && this.isOpen) {
      // iOS only presents the software keyboard when focus happens in the
      // original tap task. Open the existing Search dialog now, while this
      // click is still trusted, and let the menu finish sliding out behind it.
      // Deferring showDialog() until the 560ms drawer animation completed made
      // the input focusable but could no longer summon the keyboard.
      this.#openSearchAfterClose = true;
      removeTrapFocus();
      searchDialog.showDialog();
      this.close();
      return;
    }

    searchDialog.showDialog();
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');

    if (this.#isMobileMainDrawer(details)) {
      this.#closeMobileDrawer(details);
      return;
    }

    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * The main mobile drawer uses Web Animations so its first frame does not
   * depend on the timing of the native <details> state change.
   * @param {HTMLDetailsElement} details
   * @returns {boolean}
   */
  #isMobileMainDrawer(details) {
    return details === this.refs.details && window.matchMedia('(max-width: 749px)').matches;
  }

  #clearMobileTouchFocus(container) {
    if (!window.matchMedia('(max-width: 749px) and (pointer: coarse)').matches) return;

    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && container.contains(activeElement)) {
      activeElement.blur();
    }
  }

  /**
   * First complete the shared header motion, then expose the native details
   * drawer and start its own animation from a committed off-canvas frame.
   * @param {HTMLDetailsElement} details
   * @param {string | undefined} target
   * @param {HTMLElement} summary
   */
  async #prepareMobileDrawerOpen(details, target, summary) {
    if (
      details.dataset.stylistDrawerState === 'preparing' ||
      details.dataset.stylistDrawerState === 'opening' ||
      details.dataset.stylistDrawerState === 'open'
    ) {
      return;
    }

    const openingRevision = ++this.#mobileOpenRevision;
    details.dataset.stylistDrawerState = 'preparing';
    summary.setAttribute('aria-expanded', 'true');
    this.#clearMobileTouchFocus(details);

    const completed = await collapseMobileHeaderShell();
    if (
      !completed ||
      openingRevision !== this.#mobileOpenRevision ||
      details.dataset.stylistDrawerState !== 'preparing'
    ) {
      if (openingRevision === this.#mobileOpenRevision) {
        delete details.dataset.stylistDrawerState;
        summary.setAttribute('aria-expanded', 'false');
      }
      return;
    }

    // Match the cart drawer: establish the scroll lock before exposing the
    // panel so the first animation frame cannot be interrupted by the native
    // <details> toggle event.
    lockScroll(details);
    details.open = true;
    details.classList.remove('menu-open');
    this.preventInitialAccordionAnimations(details);
    this.#openMobileDrawer(details, target);
  }

  /**
   * Open the existing mobile drawer from the right and reveal its primary
   * navigation from underneath the fixed global logo.
   * @param {HTMLDetailsElement} details
   * @param {string | undefined} target
   */
  #openMobileDrawer(details, target) {
    this.#cancelMobileAnimations();

    const drawer = this.refs.menuDrawer;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const duration = reducedMotion ? 1 : 680;

    details.classList.add('menu-open');
    details.dataset.stylistDrawerState = 'opening';
    drawer.style.visibility = 'visible';

    if (target) {
      drawer.classList.add('menu-drawer--has-submenu-opened');
    }

    this.#drawerAnimation = drawer.animate(
      [
        { transform: 'translate3d(100%, 0, 0)' },
        { transform: 'translate3d(0, 0, 0)' },
      ],
      {
        duration,
        delay: 0,
        easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
        fill: 'both',
      }
    );

    this.#drawerAnimation.finished
      .then(() => {
        if (!details.classList.contains('menu-open')) return;
        details.dataset.stylistDrawerState = 'open';
        trapFocus(details);
      })
      .catch(() => {
        // Cancellation is expected when the user closes during the opening animation.
      });
  }

  /**
   * Close the main mobile drawer from its exact current position. This also
   * handles a close action fired before the opening animation has completed.
   * @param {HTMLDetailsElement} details
   */
  #closeMobileDrawer(details) {
    this.#mobileOpenRevision += 1;
    this.#clearMobileTouchFocus(details);
    const drawer = this.refs.menuDrawer;
    const currentTransform = getComputedStyle(drawer).transform;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const duration = reducedMotion ? 1 : 680;

    // Keep the menu content in its current visible state while the whole
    // surface leaves, just like the cart drawer. Cancelling the content
    // animations and removing `menu-open` here made the text disappear one
    // frame before the white panel started moving.
    this.#drawerAnimation?.cancel();
    this.#drawerAnimation = null;
    details.dataset.stylistDrawerState = 'closing';
    drawer.classList.remove('menu-drawer--has-submenu-opened');

    // The cart drawer releases its scroll owner before restoring the shared
    // header. Use the same ordering so a native <details> toggle cannot
    // interrupt the header handoff at a non-zero scroll position.
    unlockScroll(details);
    const headerRestorePromise = this.#openSearchAfterClose
      ? null
      : restoreMobileHeaderShell();

    this.#drawerAnimation = drawer.animate(
      [
        {
          transform:
            currentTransform === 'none' ? 'translate3d(0, 0, 0)' : currentTransform,
        },
        { transform: 'translate3d(100%, 0, 0)' },
      ],
      {
        duration,
        easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
        fill: 'both',
      }
    );

    this.#drawerAnimation.finished
      .then(async () => {
        if (details.dataset.stylistDrawerState !== 'closing') return;

        reset(details);
        delete details.dataset.stylistDrawerState;
        drawer.style.removeProperty('visibility');
        removeTrapFocus();

        const openDetails = this.querySelectorAll(
          'details[open]:not(accordion-custom > details)'
        );
        openDetails.forEach(reset);
        this.#cancelMobileAnimations();

        if (this.#openSearchAfterClose) {
          this.#openSearchAfterClose = false;
          // Search was opened synchronously in openSearch() so iOS could show
          // the keyboard. The completed menu animation only releases its
          // handoff flag; opening Search again here would re-open it when a
          // very fast user had already dismissed it.
        } else {
          if (headerRestorePromise) await headerRestorePromise;
        }
      })
      .catch(() => {
        // Cancellation is expected when a new drawer action supersedes this one.
      });
  }

  #cancelMobileAnimations() {
    this.#drawerAnimation?.cancel();
    this.#drawerAnimation = null;
    this.#drawerContentAnimations.forEach((animation) => animation.cancel());
    this.#drawerContentAnimations = [];
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
